/* eslint max-statements: ["error", {max: 23}] -- this applies for the entire file */
/* eslint max-lines-per-function: ["error", {max: 185}] -- applies to entire file */
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { BookingAvailability } from "@/constants/Booking";
import useBookingAvailabilityRequestParams from "@/hooks/booking/useBookingAvailabilityRequestParams";
import { useLazyGetBookingAvailabilityQuery } from "@/store/api/schedule/booking";
import {
  MaxAvailabilityRangeCard,
  NoAvailabilityContent,
  useBookingAvailabilityTimeSlots,
} from "@/ui/components/schedule/booking/AppointmentAvailabilityComponents";
import BookingAvailabilityDayCard from "@/ui/components/schedule/booking/BookingAvailabilityDayCard";
import { Button } from "@/ui/core";
import { FlatList, VStack } from "@/ui/layout";
import { groupTimeSlotsByDay } from "@/utils/booking.util";
import { joinTestIDs } from "@/utils/string.util";

interface AvailabilityTimeSlotListProps extends ViewWithTestID {
  isInsideBottomSheet?: boolean;
  ListHeaderComponent?: JSX.Element;
  onLoadingChange?: (isLoading: boolean) => void;
  onTimeSlotSelect?: (timeSlot?: TimeSlot) => void;
  shouldIgnoreRecurrence?: boolean;
  shouldRemoveHorizontalPadding?: boolean;
}

const AvailabilityTimeSlotList = ({
  isInsideBottomSheet = false,
  ListHeaderComponent,
  onLoadingChange,
  onTimeSlotSelect,
  shouldIgnoreRecurrence = false,
  shouldRemoveHorizontalPadding = false,
  testID,
}: AvailabilityTimeSlotListProps) => {
  const baseTestID = testID;

  const maxWeekOffset =
    BookingAvailability.maxAvailabilityWeeks -
    BookingAvailability.searchRangeInWeeks;

  const availabilityRequestParams = useBookingAvailabilityRequestParams();

  const {
    appointmentType,
    hostExternalId,
    patientExternalId,
    providerAttendeeExternalIds,
    recurrenceFrequency,
    supportAttendeeExternalIds,
  } = availabilityRequestParams || {};

  const [weekOffset, setWeekOffset] = useState<number>(0);

  const timeNow = useMemo(() => new Date(), []);

  const [timeSlot, setTimeSlot] = useState<TimeSlot | undefined>();
  const [
    shouldShowMaxAvailabilityMessage,
    setShouldShowMaxAvailabilityMessage,
  ] = useState<boolean>(false);

  const { endDateTime, startDateTime } = useBookingAvailabilityTimeSlots({
    initialDate: timeNow,
    weekOffset,
  });

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  const timeSlotGroups = useMemo(
    () => groupTimeSlotsByDay(timeSlots),
    [timeSlots],
  );

  const [getAvailability, { isSuccess }] = useLazyGetBookingAvailabilityQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setWeekOffset(0);
    setTimeSlots([]);
    onTimeSlotSelect?.(undefined);
    setTimeSlot(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldIgnoreRecurrence]);

  useEffect(() => {
    if (!appointmentType || !hostExternalId || !patientExternalId) {
      return;
    }

    setIsLoading(true);
    onLoadingChange?.(true);

    void getAvailability({
      appointmentType,
      availabilityEndDateTime: endDateTime,
      availabilityStartDateTime: startDateTime,
      hostExternalId,
      isPatientAttending: true,
      patientExternalId,
      providerAttendeeExternalIds,
      recurrenceFrequency: shouldIgnoreRecurrence
        ? undefined
        : recurrenceFrequency,
      supportAttendeeExternalIds,
      weekOffset,
    })
      .unwrap()
      .then((response) => {
        handleResponse(response);
      })
      .catch(() => {
        setIsLoading(false);
        onLoadingChange?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDateTime, startDateTime, shouldIgnoreRecurrence]);

  const handleResponse = (response: TimeSlot[]) => {
    setTimeSlots((prev) => [...prev, ...response]);

    if (!isEmpty(response) || hasReachedMaxWeekOffset) {
      setIsLoading(false);
      onLoadingChange?.(false);
    }

    if (isEmpty(response) && weekOffset < maxWeekOffset) {
      handleLoadNextAvailability();
    }

    if (hasReachedMaxWeekOffset && !isEmpty(timeSlots)) {
      setShouldShowMaxAvailabilityMessage(true);
    }
  };

  const handleTimeSlotSelect = (startTime: string) => {
    const selectedTimeSlot = timeSlots.find((ts) => ts.start === startTime);

    if (!selectedTimeSlot) {
      return;
    }
    setTimeSlot(selectedTimeSlot);
    onTimeSlotSelect?.(selectedTimeSlot);
  };

  const handleLoadNextAvailability = useCallback(() => {
    setWeekOffset((prev) => prev + BookingAvailability.searchRangeInWeeks);
  }, []);

  const hasReachedMaxWeekOffset = weekOffset >= maxWeekOffset;
  const shouldShowLoadMoreButton =
    !isEmpty(timeSlots) && isSuccess && !hasReachedMaxWeekOffset;

  const emptyListComponent = useCallback(
    () => <NoAvailabilityContent testID={baseTestID} />,
    [baseTestID],
  );

  const ListFooterComponent = useCallback(
    () => (
      <>
        {shouldShowLoadMoreButton && (
          <VStack paddingTop={12}>
            <Button
              isLoading={isLoading}
              onPress={handleLoadNextAvailability}
              testID={joinTestIDs(baseTestID, "next-week")}
              title="Load more availability"
            />
          </VStack>
        )}
        {shouldShowMaxAvailabilityMessage && (
          <MaxAvailabilityRangeCard testID={baseTestID} />
        )}
      </>
    ),
    [
      shouldShowLoadMoreButton,
      isLoading,
      handleLoadNextAvailability,
      baseTestID,
      shouldShowMaxAvailabilityMessage,
    ],
  );

  return (
    <FlatList
      data={timeSlotGroups}
      emptyListComponent={emptyListComponent}
      isDataLoading={isLoading}
      isInsideBottomSheet={isInsideBottomSheet}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={ListHeaderComponent}
      renderItem={({ item }) => {
        return (
          <BookingAvailabilityDayCard
            day={item.date}
            onSelect={handleTimeSlotSelect}
            selectedTimeSlotStartTime={timeSlot?.start}
            timeSlots={item.timeSlots}
          />
        );
      }}
      shouldDisableMaintainVisibleContentPosition
      shouldRemoveStyleHorizontalPadding={shouldRemoveHorizontalPadding}
      testID={baseTestID}
    />
  );
};

export type { AvailabilityTimeSlotListProps };
export default AvailabilityTimeSlotList;
